import {
  CURRENT_SAVE_SCHEMA_VERSION,
  parseSaveEnvelope,
  readSaveSchemaVersion,
  type SaveEnvelope,
} from "./schema.js";

export interface SaveMigration {
  readonly fromVersion: number;
  readonly toVersion: number;
  migrate(input: unknown): unknown;
}

export class SaveMigrationError extends Error {
  public constructor(
    message: string,
    public readonly fromVersion: number,
    public readonly targetVersion: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "SaveMigrationError";
  }
}

export function migrateSave(
  input: unknown,
  migrations: readonly SaveMigration[],
  targetVersion = CURRENT_SAVE_SCHEMA_VERSION,
): SaveEnvelope {
  const startVersion = readSaveSchemaVersion(input);
  if (startVersion > targetVersion) {
    throw new SaveMigrationError(
      `Save schema ${String(startVersion)} is newer than supported schema ${String(targetVersion)}.`,
      startVersion,
      targetVersion,
    );
  }

  const bySource = new Map<number, SaveMigration>();
  for (const migration of migrations) {
    if (migration.toVersion !== migration.fromVersion + 1) {
      throw new SaveMigrationError(
        `Migration ${String(migration.fromVersion)} -> ${String(migration.toVersion)} is not sequential.`,
        startVersion,
        targetVersion,
      );
    }
    if (bySource.has(migration.fromVersion)) {
      throw new SaveMigrationError(
        `More than one migration starts at schema ${String(migration.fromVersion)}.`,
        startVersion,
        targetVersion,
      );
    }
    bySource.set(migration.fromVersion, migration);
  }

  let version = startVersion;
  let migrated = input;
  while (version < targetVersion) {
    const migration = bySource.get(version);
    if (migration === undefined) {
      throw new SaveMigrationError(
        `No migration exists from save schema ${String(version)}.`,
        startVersion,
        targetVersion,
      );
    }

    try {
      migrated = migration.migrate(migrated);
      const outputVersion = readSaveSchemaVersion(migrated);
      if (outputVersion !== migration.toVersion) {
        throw new Error(
          `Migration output declared schema ${String(outputVersion)}; expected ${String(migration.toVersion)}.`,
        );
      }
      version = outputVersion;
    } catch (error) {
      throw new SaveMigrationError(
        `Migration from schema ${String(migration.fromVersion)} to ${String(migration.toVersion)} failed.`,
        startVersion,
        targetVersion,
        { cause: error },
      );
    }
  }

  try {
    return parseSaveEnvelope(migrated);
  } catch (error) {
    throw new SaveMigrationError(
      `Save failed schema ${String(targetVersion)} validation after migration.`,
      startVersion,
      targetVersion,
      { cause: error },
    );
  }
}
